import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { doc, onSnapshot, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Calendar, MapPin, Check, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import DsgvoModal from '../components/DsgvoModal';
import {
  SeminarScheduleDay,
  normalizeScheduleDays,
  sortScheduleDays,
  formatScheduleDayLine,
} from '../utils/seminarSchedule';
import {
  SeminarPromoCode,
  normalizePromoCodes,
  findMatchingPromoCode,
  calculateSeminarPricingWithPromo,
} from '../utils/seminarPromoCodes';

interface PublicEventData {
  title: string;
  date: string;
  scheduleDays?: SeminarScheduleDay[];
  location: string;
  locationAddress?: string;
  marketingTeaser?: string;
  marketingBullets?: string[];
  marketingPitch?: string;
  marketingImage?: string;
  priceBestand: number;
  priceExtern: number;
  priceZusatz: number;
  promoCodes?: SeminarPromoCode[];
}

interface RegistrationFormData {
  vorname: string;
  nachname: string;
  firma: string;
  email: string;
  telefon: string;
  strasse: string;
  plz: string;
  ort: string;
  agbAccepted: boolean;
  photoConsent: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[\d\s+-]{6,}$/;

const formatEuro = (amount: number) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);

const EMPTY_FORM: RegistrationFormData = {
  vorname: '',
  nachname: '',
  firma: '',
  email: '',
  telefon: '',
  strasse: '',
  plz: '',
  ort: '',
  agbAccepted: false,
  photoConsent: false,
};

const inputClasses =
  'bg-pe-input dark:bg-slate-900 border border-pe-border-strong dark:border-slate-700 text-pe-text dark:text-white rounded-xl p-4 w-full font-sans text-sm focus:outline-none focus:border-[#8D6435] dark:focus:border-[#E6C299] focus:ring-1 focus:ring-[#8D6435]/30 dark:focus:ring-[#E6C299]/30 transition-all';

const EventLandingPage: React.FC = () => {
  const { eventId: paramEventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const eventId = paramEventId || searchParams.get('id') || '';

  const [event, setEvent] = useState<PublicEventData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<RegistrationFormData>(EMPTY_FORM);
  const [isPitchExpanded, setIsPitchExpanded] = useState(false);
  const [isMandant, setIsMandant] = useState(false);
  const [mandantenNummer, setMandantenNummer] = useState('');
  const [zusatzPersonen, setZusatzPersonen] = useState(0);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [promoCodeError, setPromoCodeError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [isDsgvoModalOpen, setIsDsgvoModalOpen] = useState(false);

  useEffect(() => {
    if (!eventId) {
      setIsLoading(false);
      setLoadError('Keine Event-ID in der URL gefunden.');
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    const unsubscribe = onSnapshot(
      doc(db, 'public_events', eventId),
      (snapshot) => {
        if (!snapshot.exists()) {
          setEvent(null);
          setLoadError('Dieses Seminar wurde nicht gefunden.');
        } else {
          const data = snapshot.data();
          setEvent({
            title: data.title || 'Seminar',
            date: data.date || '',
            scheduleDays: normalizeScheduleDays(data.scheduleDays),
            location: data.location || '',
            locationAddress: data.locationAddress || '',
            marketingTeaser: data.marketingTeaser || '',
            marketingBullets: data.marketingBullets || [],
            marketingPitch: data.marketingPitch || '',
            marketingImage: data.marketingImage || '',
            priceBestand: Number(data.priceBestand) || 0,
            priceExtern: Number(data.priceExtern) || 0,
            priceZusatz: Number(data.priceZusatz) || 0,
            promoCodes: normalizePromoCodes(data.promoCodes),
          });
          setLoadError(null);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('EventLandingPage: Firestore-Fehler', error);
        setLoadError('Das Seminar konnte nicht geladen werden.');
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [eventId]);

  const updateField = (field: keyof RegistrationFormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (submitError) setSubmitError(null);
  };

  const matchedPromo = findMatchingPromoCode(promoCodeInput, event?.promoCodes);

  const handleApplyPromoButton = () => {
    if (!promoCodeInput.trim()) {
      setPromoCodeError(null);
      return;
    }
    if (!matchedPromo) {
      setPromoCodeError('Dieser Code ist für dieses Seminar nicht gültig.');
    } else {
      setPromoCodeError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmed = {
      vorname: form.vorname.trim(),
      nachname: form.nachname.trim(),
      firma: form.firma.trim(),
      email: form.email.trim(),
      telefon: form.telefon.trim(),
      strasse: form.strasse.trim(),
      plz: form.plz.trim(),
      ort: form.ort.trim(),
    };

    // Reset Error + highlights on submit attempt
    setSubmitError(null);
    setInvalidFields([]);

    const currentInvalidFields: string[] = [];
    const errors: string[] = [];

    const missingRequiredFields =
      !trimmed.vorname ||
      !trimmed.nachname ||
      !trimmed.firma ||
      !trimmed.strasse ||
      !trimmed.plz ||
      !trimmed.ort;

    if (!trimmed.vorname) currentInvalidFields.push('vorname');
    if (!trimmed.nachname) currentInvalidFields.push('nachname');
    if (!trimmed.firma) currentInvalidFields.push('firma');
    if (!trimmed.strasse) currentInvalidFields.push('strasse');
    if (!trimmed.plz) currentInvalidFields.push('plz');
    if (!trimmed.ort) currentInvalidFields.push('ort');

    if (missingRequiredFields) errors.push('Bitte fülle alle Pflichtfelder aus.');

    if (isMandant && !mandantenNummer.trim()) {
      currentInvalidFields.push('mandantenNummer');
      errors.push('Bitte trage deine Mandant-Nummer ein.');
    }

    if (!EMAIL_REGEX.test(trimmed.email)) {
      currentInvalidFields.push('email');
      errors.push('Bitte gib eine gültige E-Mail-Adresse ein.');
    }

    if (!trimmed.telefon || !PHONE_REGEX.test(trimmed.telefon)) {
      currentInvalidFields.push('telefon');
      errors.push('Bitte prüfe deine Telefonnummer.');
    }

    if (!form.agbAccepted) {
      currentInvalidFields.push('dsgvo');
      errors.push('Du musst den Datenschutzbestimmungen zustimmen.');
    }

    setInvalidFields(currentInvalidFields);
    if (currentInvalidFields.length > 0) {
      setSubmitError(errors[0] || null);
      return;
    }

    const pricing = calculateSeminarPricingWithPromo({
      basisPreis: isMandant ? event!.priceBestand : event!.priceExtern,
      zusatzPreisTotal: zusatzPersonen * event!.priceZusatz,
      promo: matchedPromo,
    });
    const {
      basisPreis,
      zusatzPreisTotal,
      discountNetto,
      discountLabel,
      nettoGesamt,
      mwst,
      bruttoGesamt,
    } = pricing;

    setIsSubmitting(true);

    try {
      await addDoc(collection(db, 'seminar_anmeldungen'), {
        eventId,
        status: 'neu',
        createdAt: serverTimestamp(),
        formData: {
          isMandant,
          mandantenNummer: isMandant ? mandantenNummer.trim() : '',
          vorname: trimmed.vorname,
          nachname: trimmed.nachname,
          firma: trimmed.firma,
          zusatzPersonen,
          promoCode: matchedPromo?.code || '',
          email: trimmed.email,
          telefon: trimmed.telefon,
          strasse: trimmed.strasse,
          plz: trimmed.plz,
          ort: trimmed.ort,
        },
        financials: {
          basisPreis,
          zusatzPreisTotal,
          promoCode: matchedPromo?.code || '',
          discountNetto,
          discountLabel,
          nettoGesamt,
          mwst,
          bruttoGesamt,
        },
        legal: {
          dsgvoAkzeptiert: form.agbAccepted,
          fotoVideoAkzeptiert: form.photoConsent,
        },
      });

      // Admin-Hinweis (zusätzlich zum Rechnungs-Creator-Badge)
      try {
        const eventTitle = String(event?.title || 'Seminar').trim();
        await addDoc(collection(db, 'mail_queue'), {
          type: 'change_request',
          email: 'erfolg@friseur-coaching.de',
          subject: `Neue Seminar-Anmeldung: ${eventTitle}`,
          messageHtml: [
            `<p><strong>Neue Anmeldung</strong> für <strong>${eventTitle}</strong>.</p>`,
            `<p>${trimmed.vorname} ${trimmed.nachname}${trimmed.firma ? ` (${trimmed.firma})` : ''}<br/>${trimmed.email}<br/>Brutto: ${bruttoGesamt.toFixed(2)} €</p>`,
            `<p>Bitte im Admin → Rechnungs-Creator verarbeiten.</p>`,
          ].join(''),
          mailTitle: 'Seminar-Anmeldung',
          participantName: `${trimmed.vorname} ${trimmed.nachname}`.trim(),
          eventName: eventTitle,
          appBaseUrl: window.location.origin,
          status: 'pending',
          createdAt: serverTimestamp(),
        });
      } catch (mailErr) {
        console.warn('Admin-Hinweismail fehlgeschlagen (Anmeldung ist gespeichert):', mailErr);
      }

      setSubmitSuccess(true);
      setForm(EMPTY_FORM);
      setIsMandant(false);
      setMandantenNummer('');
      setZusatzPersonen(0);
      setPromoCodeInput('');
      setPromoCodeError(null);
    } catch (error) {
      console.error('EventLandingPage: Anmeldung fehlgeschlagen', error);
      setSubmitError('Die Anmeldung konnte nicht gespeichert werden. Bitte versuche es erneut oder kontaktiere uns direkt.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pe-bg dark:bg-[#030303] text-pe-muted dark:text-slate-300 font-sans flex items-center justify-center p-8">
        <div className="max-w-3xl w-full space-y-6">
          <div className="flex items-center justify-center gap-3 text-pe-accent-text dark:text-[#E6C299] mb-8">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm font-bold uppercase tracking-widest">Seminar wird geladen…</span>
          </div>
          <div className="h-12 bg-stone-200/70 dark:bg-slate-800/50 rounded-xl animate-pulse" />
          <div className="h-6 bg-stone-200/60 dark:bg-slate-800/40 rounded-lg animate-pulse w-2/3" />
          <div className="h-32 bg-stone-200/50 dark:bg-slate-800/30 rounded-2xl animate-pulse" />
          <div className="h-48 bg-stone-200/50 dark:bg-slate-800/30 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="min-h-screen bg-pe-bg dark:bg-[#030303] text-pe-muted dark:text-slate-300 font-sans flex items-center justify-center p-8">
        <div className="max-w-md text-center bg-pe-card dark:bg-[#0a0a0a] border border-pe-border dark:border-[#333] rounded-2xl p-10 shadow-xl">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-pe-text dark:text-white mb-2">Seminar nicht verfügbar</h1>
          <p className="text-pe-muted dark:text-slate-400 text-sm">{loadError || 'Unbekannter Fehler.'}</p>
        </div>
      </div>
    );
  }

  const pricing = calculateSeminarPricingWithPromo({
    basisPreis: isMandant ? event.priceBestand : event.priceExtern,
    zusatzPreisTotal: zusatzPersonen * event.priceZusatz,
    promo: matchedPromo,
  });
  const {
    basisPreis,
    zusatzPreisTotal,
    discountNetto,
    discountLabel,
    nettoGesamt,
    mwst,
    bruttoGesamt,
  } = pricing;
  return (
    <div className="min-h-screen bg-pe-bg dark:bg-[#030303] text-pe-muted dark:text-slate-300 font-sans">
      {/* ZONE 1: HERO */}
      <header className="px-4 md:px-8 pt-12 md:pt-16 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center max-w-7xl mx-auto">
          <div className="text-left">
            <span className="inline-block text-[10px] font-black uppercase tracking-[0.3em] text-pe-accent-text dark:text-[#E6C299] border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-full mb-6">
              PreisExpert Seminar
            </span>
            <h1 className="text-5xl lg:text-6xl font-black text-pe-text dark:text-white leading-tight tracking-tight mb-4">
              {event.title}
            </h1>

            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mb-6">
              {(() => {
                const days = sortScheduleDays(normalizeScheduleDays(event.scheduleDays));
                if (days.length > 0) {
                  return days.map((day) => (
                    <span
                      key={day.id}
                      className="inline-flex items-center gap-2 text-sm text-pe-muted border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-lg"
                    >
                      <Calendar className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299] shrink-0" />
                      {formatScheduleDayLine(day)}
                    </span>
                  ));
                }
                if (event.date) {
                  return (
                    <span className="inline-flex items-center gap-2 text-sm text-pe-muted border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-lg">
                      <Calendar className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299]" />
                      {event.date}
                    </span>
                  );
                }
                return null;
              })()}
              {(event.location || event.locationAddress) && (
                <span className="inline-flex items-center gap-2 text-sm text-pe-muted border border-pe-border dark:border-[#333] bg-stone-100 dark:bg-[#0a0a0a] px-3 py-1.5 rounded-lg">
                  <MapPin className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299]" />
                  {event.locationAddress || event.location}
                </span>
              )}
            </div>

            {event.marketingTeaser && (
              <p className="text-pe-muted text-lg lg:text-xl mt-6 leading-relaxed whitespace-pre-line">
                {event.marketingTeaser}
              </p>
            )}
          </div>

          {event.marketingImage && (
            <div className="flex justify-center items-center">
              <img
                src={event.marketingImage}
                alt={event.title}
                className="max-w-full max-h-[75vh] w-auto h-auto rounded-3xl shadow-2xl border border-pe-border dark:border-slate-800"
              />
            </div>
          )}
        </div>
      </header>

      {/* Info-Kacheln: Hero-Raster, Grid-Karten statt aufgeblasener Einzelliste */}
      {(event.marketingBullets?.length || event.marketingPitch) ? (
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-4 md:pt-8 pb-8 md:pb-10">
          {event.marketingBullets && event.marketingBullets.length > 0 && (
            <section className="mb-10">
              <div className="flex items-center gap-4 mb-6">
                <h2 className="text-[11px] font-black uppercase tracking-[0.28em] text-pe-accent-text dark:text-[#E6C299] shrink-0">
                  Agenda & Highlights
                </h2>
                <div className="h-px flex-1 bg-gradient-to-r from-[#E6C299]/40 to-transparent" />
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                {event.marketingBullets.map((bullet, index) => (
                  <li
                    key={index}
                    className="flex gap-3.5 items-start rounded-2xl border border-pe-border dark:border-slate-800/80 bg-white/80 dark:bg-[#0a0a0a]/80 px-5 py-4 shadow-[inset_0_1px_0_rgba(230,194,153,0.06)] hover:border-[#8D6435]/25 dark:hover:border-[#E6C299]/25 transition-colors"
                  >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#8D6435]/35 dark:border-[#E6C299]/35 bg-[#E6C299]/10">
                      <Check className="w-4 h-4 text-pe-accent-text dark:text-[#E6C299]" strokeWidth={2.5} />
                    </span>
                    <span className="text-pe-muted dark:text-slate-300 text-[15px] md:text-base leading-snug whitespace-pre-line pt-1">
                      {bullet}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {event.marketingPitch && (
            <section className="mb-2 rounded-2xl border border-pe-border dark:border-slate-800/80 bg-white/60 dark:bg-[#0a0a0a]/60 overflow-hidden">
              <div className="flex items-center gap-4 px-6 md:px-8 pt-6 pb-2">
                <h2 className="text-[11px] font-black uppercase tracking-[0.28em] text-pe-accent-text dark:text-[#E6C299] shrink-0">
                  Über dieses Seminar
                </h2>
                <div className="h-px flex-1 bg-gradient-to-r from-[#E6C299]/40 to-transparent" />
              </div>
              <div className="px-6 md:px-8 pb-6 md:pb-8">
                <div
                  className={`relative pl-5 border-l-2 border-[#8D6435]/40 dark:border-[#E6C299]/40 text-pe-muted dark:text-slate-300 text-[15px] md:text-base leading-relaxed whitespace-pre-line ${
                    !isPitchExpanded ? 'line-clamp-4' : ''
                  }`}
                >
                  {event.marketingPitch}
                </div>
                <button
                  type="button"
                  onClick={() => setIsPitchExpanded((prev) => !prev)}
                  className="mt-5 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-pe-accent-text dark:text-[#E6C299] hover:text-pe-text dark:hover:text-white transition-colors"
                >
                  MEHR ERFAHREN
                  <ChevronDown className={`w-4 h-4 transition-transform ${isPitchExpanded ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </section>
          )}
        </div>
      ) : null}

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <section className="mb-8 border border-pe-border dark:border-slate-800/60 rounded-2xl p-6 md:p-8 bg-transparent">
          <h2 className="text-sm font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] mb-4">Preise</h2>
          <ul className="space-y-2 text-sm text-pe-muted dark:text-slate-300">
            <li>Preis für PreisExpert Mandanten: <span className="text-pe-text dark:text-white font-semibold">{formatEuro(event.priceBestand)}</span> (netto)</li>
            <li>Regulärer Seminar-Preis: <span className="text-pe-text dark:text-white font-semibold">{formatEuro(event.priceExtern)}</span> (netto)</li>
            <li>Preis pro Zusatz-Teilnehmer (Mitarbeiter): <span className="text-pe-text dark:text-white font-semibold">{formatEuro(event.priceZusatz)}</span> (netto)</li>
          </ul>
        </section>

        {/* ZONE 2: ANMELDE-FORMULAR & DSGVO */}
        <section className="bg-pe-card dark:bg-[#0a0a0a] border border-[#8D6435]/20 dark:border-[#E6C299]/20 rounded-2xl p-6 md:p-8 shadow-[0_0_40px_rgba(230,194,153,0.05)]">
          <h2 className="text-xl font-bold text-pe-text dark:text-white mb-2">Jetzt anmelden</h2>
          <p className="text-stone-500 dark:text-slate-500 text-sm mb-6">Sichere dir deinen Platz — wir melden uns zeitnah bei dir.</p>

          {submitSuccess ? (
            <div className="text-center py-8 px-4 bg-emerald-950/30 border border-emerald-800/50 rounded-xl">
              <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-pe-text dark:text-white mb-2">Anmeldung erfolgreich!</h3>
              <p className="text-pe-muted dark:text-slate-400 text-sm leading-relaxed">
                Vielen Dank für deine Anmeldung zu „{event.title}". Wir haben deine Daten erhalten und melden uns zeitnah bei dir.
              </p>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Bist du bereits PreisExpert Mandant? *</label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => { setIsMandant(false); setMandantenNummer(''); if (submitError) setSubmitError(null); }}
                  className={`p-4 rounded-xl border-2 font-bold uppercase tracking-wider text-sm transition-all ${!isMandant ? 'bg-[#E6C299]/10 border-[#8D6435] dark:border-[#E6C299] text-pe-accent-text dark:text-[#E6C299] shadow-[0_0_15px_rgba(230,194,153,0.1)]' : 'bg-pe-input dark:bg-slate-900 border-pe-border-strong dark:border-slate-700 text-pe-muted dark:text-slate-400 hover:border-stone-400 dark:hover:border-slate-600'}`}
                >
                  Nein
                </button>
                <button
                  type="button"
                  onClick={() => { setIsMandant(true); if (submitError) setSubmitError(null); }}
                  className={`p-4 rounded-xl border-2 font-bold uppercase tracking-wider text-sm transition-all ${isMandant ? 'bg-[#E6C299]/10 border-[#8D6435] dark:border-[#E6C299] text-pe-accent-text dark:text-[#E6C299] shadow-[0_0_15px_rgba(230,194,153,0.1)]' : 'bg-pe-input dark:bg-slate-900 border-pe-border-strong dark:border-slate-700 text-pe-muted dark:text-slate-400 hover:border-stone-400 dark:hover:border-slate-600'}`}
                >
                  Ja
                </button>
              </div>
            </div>

            {isMandant && (
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Mandant-Nummer *</label>
                <input
                  type="text"
                  value={mandantenNummer}
                  onChange={(e) => { setMandantenNummer(e.target.value); if (submitError) setSubmitError(null); }}
                  placeholder="z.B. M-12345"
                  className={
                    invalidFields.includes('mandantenNummer')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Vorname *</label>
                <input
                  type="text"
                  value={form.vorname}
                  onChange={(e) => updateField('vorname', e.target.value)}
                  className={
                    invalidFields.includes('vorname')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
              </div>
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Nachname *</label>
                <input
                  type="text"
                  value={form.nachname}
                  onChange={(e) => updateField('nachname', e.target.value)}
                  className={
                    invalidFields.includes('nachname')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
              </div>
            </div>

            <div>
              <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Salon / Firma *</label>
              <input
                type="text"
                value={form.firma}
                onChange={(e) => updateField('firma', e.target.value)}
                className={
                  invalidFields.includes('firma')
                    ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                    : inputClasses
                }
              />
            </div>

            <div>
              <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Wie viele Mitarbeiter bringst du zusätzlich mit?</label>
              <div className="flex items-center justify-center gap-6">
                <button
                  type="button"
                  onClick={() => setZusatzPersonen((prev) => Math.max(0, prev - 1))}
                  className="w-14 h-14 flex items-center justify-center bg-stone-200 dark:bg-slate-800 hover:bg-stone-300 dark:hover:bg-slate-700 text-pe-accent-text dark:text-[#E6C299] text-2xl font-bold rounded-xl border border-pe-border-strong dark:border-slate-700 transition-colors"
                >
                  −
                </button>
                <span className="text-4xl font-black text-pe-text dark:text-white w-16 text-center tabular-nums">{zusatzPersonen}</span>
                <button
                  type="button"
                  onClick={() => setZusatzPersonen((prev) => prev + 1)}
                  className="w-14 h-14 flex items-center justify-center bg-stone-200 dark:bg-slate-800 hover:bg-stone-300 dark:hover:bg-slate-700 text-pe-accent-text dark:text-[#E6C299] text-2xl font-bold rounded-xl border border-pe-border-strong dark:border-slate-700 transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">E-Mail *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={
                    invalidFields.includes('email')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
              </div>
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Telefon (WhatsApp) *</label>
                <input
                  type="tel"
                  value={form.telefon}
                  onChange={(e) => updateField('telefon', e.target.value)}
                  className={
                    invalidFields.includes('telefon')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
                <p className="text-stone-500 dark:text-slate-500 text-xs mt-2">Für wichtige Updates und die Event-WhatsApp-Gruppe.</p>
              </div>
            </div>

            <div>
              <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Straße *</label>
              <input
                type="text"
                value={form.strasse}
                onChange={(e) => updateField('strasse', e.target.value)}
                className={
                  invalidFields.includes('strasse')
                    ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                    : inputClasses
                }
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">PLZ *</label>
                <input
                  type="text"
                  value={form.plz}
                  onChange={(e) => updateField('plz', e.target.value)}
                  className={
                    invalidFields.includes('plz')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
              </div>
              <div>
                <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">Ort *</label>
                <input
                  type="text"
                  value={form.ort}
                  onChange={(e) => updateField('ort', e.target.value)}
                  className={
                    invalidFields.includes('ort')
                      ? `${inputClasses} border-red-500 bg-red-950/20 ring-1 ring-red-500`
                      : inputClasses
                  }
                />
              </div>
            </div>

            <div className="pt-4 space-y-3 border-t border-pe-border dark:border-slate-800">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.agbAccepted}
                  onChange={(e) => updateField('agbAccepted', e.target.checked)}
                  className={
                    invalidFields.includes('dsgvo')
                      ? 'mt-1 accent-[#E6C299] w-4 h-4 shrink-0 ring-1 ring-red-500'
                      : 'mt-1 accent-[#E6C299] w-4 h-4 shrink-0'
                  }
                />
                <span
                  className={
                    invalidFields.includes('dsgvo')
                      ? 'text-sm text-red-400 group-hover:text-red-300 transition-colors'
                      : 'text-sm text-pe-muted dark:text-slate-400 group-hover:text-stone-700 dark:group-hover:text-slate-300 transition-colors'
                  }
                >
                  Ich habe die{' '}
                  <button type="button" onClick={() => setIsDsgvoModalOpen(true)} className="text-pe-accent-text dark:text-[#E6C299] hover:underline">Datenschutzbestimmungen</button>
                  {' '}gelesen und akzeptiere diese. *
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.photoConsent}
                  onChange={(e) => updateField('photoConsent', e.target.checked)}
                  className="mt-1 accent-[#E6C299] w-4 h-4 shrink-0"
                />
                <span className="text-sm text-pe-muted dark:text-slate-400 group-hover:text-stone-700 dark:group-hover:text-slate-300 transition-colors">
                  Ich stimme zu, dass während des Events Foto- und Videoaufnahmen für Marketingzwecke gemacht werden dürfen. (optional)
                </span>
              </label>
            </div>

            {/* EINLADUNGS- / RABATTCODE */}
            <div className="pt-4 border-t border-pe-border dark:border-slate-800">
              <label className="block text-pe-muted dark:text-slate-400 text-xs font-semibold uppercase tracking-wider mb-2">
                Einladungs- oder Rabattcode (optional)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCodeInput}
                  onChange={(e) => {
                    setPromoCodeInput(e.target.value.toUpperCase());
                    if (promoCodeError) setPromoCodeError(null);
                  }}
                  placeholder="Code eingeben (z. B. VIP100)"
                  className={`${inputClasses} uppercase font-mono`}
                />
                <button
                  type="button"
                  onClick={handleApplyPromoButton}
                  className="shrink-0 px-5 rounded-xl border border-[#E6C299]/50 bg-[#E6C299]/15 hover:bg-[#E6C299] text-pe-accent-text dark:text-[#E6C299] hover:text-black text-xs font-black uppercase tracking-widest transition-colors"
                >
                  Einlösen
                </button>
              </div>
              {matchedPromo && (
                <p className="mt-2 text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                  <Check className="w-4 h-4 shrink-0" />
                  Code eingelöst: {discountLabel}
                </p>
              )}
              {!matchedPromo && promoCodeError && (
                <p className="mt-2 text-xs font-semibold text-red-500 dark:text-red-400">
                  {promoCodeError}
                </p>
              )}
            </div>

            <div className="mt-4 p-5 border border-pe-border dark:border-slate-800 rounded-xl bg-stone-100/60 dark:bg-black/30 space-y-2 text-sm">
              <div className="flex justify-between text-pe-muted dark:text-slate-400">
                <span>Seminar-Preis ({isMandant ? 'Mandant' : 'Regulär'}, netto)</span>
                <span className="text-pe-text dark:text-white font-mono">{formatEuro(basisPreis)}</span>
              </div>
              {zusatzPersonen > 0 && (
                <div className="flex justify-between text-pe-muted dark:text-slate-400">
                  <span>Zusatz-Teilnehmer ({zusatzPersonen} × {formatEuro(event.priceZusatz)})</span>
                  <span className="text-pe-text dark:text-white font-mono">{formatEuro(zusatzPreisTotal)}</span>
                </div>
              )}
              {discountNetto > 0 && (
                <div className="flex justify-between text-emerald-700 dark:text-emerald-400 font-semibold">
                  <span>Rabatt ({matchedPromo?.code})</span>
                  <span className="font-mono">−{formatEuro(discountNetto)}</span>
                </div>
              )}
              <div className="flex justify-between text-pe-muted dark:text-slate-400 border-t border-pe-border dark:border-slate-800 pt-2">
                <span>Zwischensumme (netto)</span>
                <span className="text-pe-text dark:text-white font-mono">{formatEuro(nettoGesamt)}</span>
              </div>
              <div className="flex justify-between text-pe-muted dark:text-slate-400">
                <span>MwSt. (19%)</span>
                <span className="text-pe-text dark:text-white font-mono">{formatEuro(mwst)}</span>
              </div>
              <div className="flex justify-between text-pe-accent-text dark:text-[#E6C299] font-bold border-t border-pe-border-strong dark:border-slate-700 pt-2 text-base">
                <span>Gesamtbetrag (brutto)</span>
                <span className="font-mono">{formatEuro(bruttoGesamt)}</span>
              </div>
            </div>

            {submitError && (
              <div className="flex items-start gap-3 bg-red-950/30 border border-red-800/50 text-red-300 text-sm rounded-xl p-4">
                <AlertCircle className="w-5 h-5 shrink-0 mt-0.5 text-red-400" />
                <span>{submitError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-4 bg-[#E6C299] hover:bg-[#d9b183] dark:hover:bg-white text-black font-black uppercase tracking-widest py-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Wird gesendet…
                </>
              ) : (
                'Verbindlich anmelden'
              )}
            </button>
          </form>
          )}
        </section>

        <footer className="mt-12 pt-6 border-t border-pe-border dark:border-slate-800 text-center text-xs text-stone-500 dark:text-slate-600">
          <a href="https://www.friseur-coaching.de/agb" target="_blank" rel="noopener noreferrer" className="hover:text-[#8D6435] dark:hover:text-[#E6C299] transition-colors">AGB</a>
          <span className="mx-3">·</span>
          <button type="button" onClick={() => setIsDsgvoModalOpen(true)} className="hover:text-[#8D6435] dark:hover:text-[#E6C299] transition-colors">Datenschutz</button>
        </footer>
      </div>

      <DsgvoModal isOpen={isDsgvoModalOpen} onClose={() => setIsDsgvoModalOpen(false)} />
    </div>
  );
};

export default EventLandingPage;
